import type { SearchSliceNode, SliceMetrics, StreamSliceNode } from '@/analysis/searchContract'
import { typeIdForStream } from '@/lib/typeId'

/**
 * Pure mapping: stream slice nodes → deep-dive table rows.
 *
 * Honest-labeling contract (see column defs in DeepDiveTable):
 *  - jitter / packetDelay / dropFrame / I/P frame delay are DERIVED PROXIES
 *    computed by the aggregator from pipeline flow counters, not player truth.
 *  - iFrameCount / pFrameCount / bufferOverRun / bufferUnderRunMs / avgFrameBytes
 *    are real v2 player telemetry — null for old players.
 *  - B-frame count is structurally "n/a": FLV frame types are key/inter only.
 */
export type DeepDiveRow = {
  id: string
  streamId: string
  /** Catalog deviceQuality label ("Mobile"); null = uncatalogued. */
  typeId: string | null
  /** Protocol / transport (FLV, HLS, Millicast, …) from slice/catalog. */
  streamType: string | null
  severity: 0 | 1 | 2 | null
  worstMetricSeverity: 0 | 1 | 2 | null
  worstMetric: string | null
  usersTotal: number | null
  windowStartMs: number | null
  windowEndMs: number | null
  status: 'online' | 'idle' | 'recently-offline' | null
  lastSeenMs: number | null
  metrics: SliceMetrics | null
}

export function toDeepDiveRow(n: StreamSliceNode): DeepDiveRow {
  const sid = String(n.streamId || n.value || '').trim()
  const start = Number.isFinite(n.windowStartMs) ? n.windowStartMs : null
  const end = Number.isFinite(n.windowEndMs) ? n.windowEndMs : null
  const worst =
    n.worstMetricSeverity === 0 || n.worstMetricSeverity === 1 || n.worstMetricSeverity === 2
      ? n.worstMetricSeverity
      : null
  return {
    id: `${sid}:${end ?? start ?? 0}`,
    streamId: sid,
    typeId: typeIdForStream(sid),
    streamType: n.streamType ? String(n.streamType).trim() || null : null,
    severity: n.avgSeverity ?? null,
    worstMetricSeverity: worst,
    worstMetric: n.worstMetric ?? null,
    usersTotal: typeof n.usersTotal === 'number' && Number.isFinite(n.usersTotal) ? n.usersTotal : null,
    windowStartMs: start,
    windowEndMs: end,
    status: n.status ?? null,
    lastSeenMs: n.lastSeenMs ?? null,
    metrics: n.metrics ?? null,
  }
}

/**
 * Every analysis window in scope (not latest-only).
 * Drilled stream → that stream’s windows; undrilled → all stream nodes.
 * Newest windowEndMs first.
 */
export function deepDiveRowsFromNodes(
  nodes: ReadonlyArray<SearchSliceNode> | null | undefined,
  drillStream: string | null,
): DeepDiveRow[] {
  const streams = (nodes ?? []).filter((n): n is StreamSliceNode => n.kind === 'stream')
  const drill = String(drillStream ?? '').trim()
  const scoped = drill
    ? streams.filter((n) => n.streamId === drill || n.value === drill)
    : streams

  return scoped
    .filter((n) => String(n.streamId || n.value || '').trim())
    .map(toDeepDiveRow)
    .sort((a, b) => {
      const de = Number(b.windowEndMs ?? 0) - Number(a.windowEndMs ?? 0)
      if (de !== 0) return de
      const ds = Number(b.windowStartMs ?? 0) - Number(a.windowStartMs ?? 0)
      if (ds !== 0) return ds
      return a.streamId.localeCompare(b.streamId)
    })
}

const MAX_BUFFERED_ROWS = 20_000

/**
 * Upsert live/analysis windows into a retained buffer.
 * Same `id` (streamId:windowEndMs) updates the active row; a new window
 * pushes history. Older rows outside bufferMs from the newest end are dropped.
 */
export function mergeDeepDiveRows(
  prev: ReadonlyArray<DeepDiveRow>,
  incoming: ReadonlyArray<DeepDiveRow>,
  bufferMs: number,
): DeepDiveRow[] {
  const map = new Map<string, DeepDiveRow>()
  for (const r of prev) map.set(r.id, r)
  for (const r of incoming) map.set(r.id, r)

  let arr = [...map.values()].sort(
    (a, b) => Number(a.windowEndMs ?? 0) - Number(b.windowEndMs ?? 0),
  )

  if (bufferMs !== Infinity && arr.length) {
    const newest = Number(arr[arr.length - 1]!.windowEndMs ?? 0)
    const cutoff = newest - bufferMs
    arr = arr.filter((r) => Number(r.windowEndMs ?? 0) >= cutoff)
  }
  if (arr.length > MAX_BUFFERED_ROWS) {
    arr = arr.slice(arr.length - MAX_BUFFERED_ROWS)
  }
  return arr
}
